import { useEffect, useRef, useState } from "react";
import { syncCall } from "../../../lib/admin";
import { SyncEntity } from "../../../lib/dtos/storeSyncRequest";
import { Store } from "../../../types";
import { useAuth } from "../../../utils/authorization";
import { onSyncWindowChanged, openSyncWindow, syncWindowRemainingMs } from "../syncWindow";

/**
 * Force-syncs a given entity for a store (all rows or a selection). `syncing` is true while
 * the store is already syncing — same criteria as the store cards (`syncState`/`syncV2IsRunning`)
 * — and for a minute right after starting, so the action can't be triggered again mid-sync. That
 * window is stored in localStorage, so it keeps showing after a page refresh.
 */
export function useForceSync(store: Store, entity: SyncEntity) {
    const { user } = useAuth();
    const [isOptimisticallySyncing, setIsOptimisticallySyncing] = useState(() => syncWindowRemainingMs(store.id) > 0);
    const timerRef = useRef<ReturnType<typeof setTimeout>>();

    /** Re-reads the persisted optimistic window into state and clears it once the window elapses. */
    const updateStoreSyncingState = () => {
        clearTimeout(timerRef.current);
        const remaining = syncWindowRemainingMs(store.id);
        setIsOptimisticallySyncing(remaining > 0);
        if (remaining > 0) {
            timerRef.current = setTimeout(() => setIsOptimisticallySyncing(false), remaining);
        }
    };

    useEffect(() => {
        updateStoreSyncingState();
        return () => clearTimeout(timerRef.current);
    }, [store.id, store.syncState, store.syncV2IsRunning]);

    const refreshRef = useRef(updateStoreSyncingState);
    refreshRef.current = updateStoreSyncingState;

    useEffect(() => onSyncWindowChanged(store.id, () => refreshRef.current()), [store.id]);

    const isSyncing =
        store.syncState === 1 ||
        store.syncV2IsRunning === 1 ||
        isOptimisticallySyncing;

    /** Triggers the force sync. Resolves to "" on success, or to an error message to display. */
    const triggerForceSync = async (entityIds?: number[]): Promise<string> => {
        clearTimeout(timerRef.current);
        setIsOptimisticallySyncing(true);
        try {
            const result = await syncCall(store.id, {
                email: user?.email,
                entity,
                syncEntityModifier: 'Force',
                ...(entityIds && { entityIds }),
            });
            if (result !== "") {
                setIsOptimisticallySyncing(false);
                return typeof result === "string" ? result : (result?.message ?? result?.error ?? "Force sync failed.");
            }
            openSyncWindow(store.id);
            updateStoreSyncingState();
            return "";
        } catch {
            setIsOptimisticallySyncing(false);
            return "Force sync failed. Please try again later.";
        }
    };

    const forceSyncAll = () => triggerForceSync();

    const forceSyncSelection = (entityIds: number[]) => triggerForceSync(entityIds);

    return { forceSyncAll, forceSyncSelection, syncing: isSyncing };
}
